import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiPhone, FiMail, FiFacebook, FiTwitter, FiInstagram, FiMapPin } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Why Us', href: '#why-choose-us' },
    { label: 'Contact', href: '#contact' }
  ];

  return (
    <>
      {/* Top Bar - Hidden on mobile, visible on lg */}
      <div className="bg-slate-900 text-slate-300 py-3 hidden lg:block">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            <a href="mailto:dipaksahani229@gmail.com" className="flex items-center hover:text-emerald-400 transition-colors">
              <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
              dipaksahani229@gmail.com
            </a>
            <span className="flex items-center">
              <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
              108 - B Complex, Surat
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="mr-2">Follow Us:</span>
            <a href="#" className="hover:text-emerald-400"><SafeIcon icon={FiFacebook} /></a>
            <a href="#" className="hover:text-emerald-400"><SafeIcon icon={FiTwitter} /></a>
            <a href="#" className="hover:text-emerald-400"><SafeIcon icon={FiInstagram} /></a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${isScrolled ? 'shadow-md py-2' : 'shadow-sm py-3 lg:py-4'}`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.a 
              href="#home" 
              className="flex items-center space-x-2 z-50 relative"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-bold shadow-md text-sm lg:text-base">
                DIP
              </div>
              <div className="flex flex-col">
                <span className="text-xl lg:text-2xl font-bold text-slate-900 leading-none tracking-tight">
                  DIP<span className="text-emerald-600">Physio</span>
                </span>
                <span className="text-[9px] lg:text-[10px] text-slate-500 font-bold tracking-[0.2em] uppercase">Medical Clinic</span>
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="font-bold text-slate-700 hover:text-emerald-600 transition-colors duration-200 text-sm uppercase tracking-wide"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center">
              <div className="flex items-center mr-6 border-r border-slate-200 pr-6">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mr-3">
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                </div>
                <div>
                  <span className="block text-xs text-slate-500 font-bold">Call Anytime</span>
                  <span className="block text-sm font-bold text-slate-900">95109 18372</span>
                </div>
              </div>
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-emerald-700 transition-colors duration-200 shadow-lg shadow-emerald-600/20"
              >
                APPOINTMENT
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:text-emerald-600 focus:outline-none z-50 relative"
              aria-label="Toggle Menu"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-7 h-7" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: '100vh' }}
              exit={{ opacity: 0, height: 0 }}
              className="fixed inset-0 top-0 left-0 w-full bg-white z-40 lg:hidden overflow-y-auto pt-20"
            >
              <div className="container mx-auto px-4 py-6 flex flex-col h-full">
                <nav className="flex flex-col space-y-1">
                  {navItems.map((item, index) => (
                    <motion.a
                      key={item.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      href={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-slate-800 hover:text-emerald-600 font-bold text-xl py-4 border-b border-slate-50 uppercase tracking-wide"
                    >
                      {item.label}
                    </motion.a>
                  ))}
                </nav>
                
                <div className="mt-8 space-y-6">
                  <div className="flex items-center space-x-4 bg-slate-50 p-4 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                      <SafeIcon icon={FiPhone} className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-xs text-slate-500 font-bold uppercase">Call Anytime</span>
                      <a href="tel:9510918372" className="block text-lg font-bold text-slate-900">95109 18372</a>
                    </div>
                  </div>

                  <a
                    href="#contact"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full bg-emerald-600 text-white px-6 py-4 rounded-xl font-bold text-center shadow-lg uppercase text-sm tracking-widest hover:bg-emerald-700 transition-colors"
                  >
                    Book Appointment
                  </a>
                  
                  <div className="flex justify-center space-x-8 pt-4">
                    <a href="#" className="text-slate-400 hover:text-emerald-600"><SafeIcon icon={FiFacebook} className="w-6 h-6" /></a>
                    <a href="#" className="text-slate-400 hover:text-emerald-600"><SafeIcon icon={FiTwitter} className="w-6 h-6" /></a>
                    <a href="#" className="text-slate-400 hover:text-emerald-600"><SafeIcon icon={FiInstagram} className="w-6 h-6" /></a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default Header;